/**
 * `useTenantCurrency()` — the tenant-currency SOURCE for v2 money rendering
 * (DNKADM-0050).
 *
 * `formatMoney(amount, currencyCode)` (`src/utils/v2Format.ts`) takes the ISO-4217
 * currency CODE as an argument — never assuming `$`. This hook is the single SOURCE
 * of that code: every v2 money-rendering screen (0050–0062) reads it and passes the
 * code into `formatMoney`. It is named EXACTLY `useTenantCurrency` because downstream
 * specs depend on the name.
 *
 * Sourced from `getTenantSettings()` (handoff §5.1) through the established
 * `useResource` resource pattern — load on mount, expose `{ currency, status,
 * error, refetch }`. While loading or on error, `currency` is `null` so a consumer
 * can hold off rendering money (or fall back) rather than format against a wrong
 * code. The Tenant Settings screen also writes the currency (setup-only); after a
 * change it `refetch`es so consumers re-read the new code.
 *
 * No client store/cache layer beyond `useResource` (the repo uses no query client /
 * state store — see the react-dev guidance); each mount loads live and a `refetch`
 * re-reads live, exactly like every other resource.
 *
 * **DNKADM-0364 — `enabled`.** A caller that knows this session cannot read
 * `/v2/admin/tenant-settings` passes `{ enabled: false }` and the hook issues NO
 * request at all, returning the same ready/null shape a consumer already reads
 * before the code resolves. Mirrors the `NotificationsProvider` `enabled` idiom
 * (DNKADM-0359): the machinery still mounts, only the load is skipped.
 */
import { useCallback } from 'react';
import { useResource, type ResourceStatus } from './useResource';
import { getTenantSettings } from '@/api/tenantSettings';

export interface TenantCurrency {
  /**
   * The tenant ISO-4217 currency code (e.g. `USD`), or `null` until the first
   * successful load (or on error) — a consumer holds money rendering until it is
   * non-null rather than format against a wrong code.
   */
  currency: string | null;
  /** Coarse load state the UI switches on. */
  status: ResourceStatus;
  /** The error from the most recent failed load, else `null`. */
  error: unknown;
  /** Stable re-read (e.g. after the setup-only currency change). */
  refetch: () => void;
}

export interface UseTenantCurrencyOptions {
  /**
   * Load the tenant currency. Defaults to `true`. DNKADM-0364 had the reservations
   * drawer pass `false` for a STAFF session; DNKADM-0375 found `GET
   * /v2/admin/tenant-settings` open to `STAFF_ROLES`, so the drawer no longer passes
   * it and no production caller sets `false` today. Kept for a caller that must not
   * ask.
   */
  enabled?: boolean;
}

/**
 * The disabled `refetch` — a real no-op, so a disabled caller can never fetch.
 * Module-level, so its identity is stable across renders: a consumer may hold
 * `refetch` in a dependency array (the Tenant Settings screen re-reads with it after
 * the setup-only currency write).
 */
const NOOP_REFETCH = () => undefined;

/**
 * Load the tenant currency code from `getTenantSettings()`. The returned `currency`
 * is `null` while loading / on error and the resolved ISO-4217 code once ready.
 *
 * With `{ enabled: false }` nothing is requested and the value is the ready/null
 * shape (`{ currency: null, status: 'ready', error: null, refetch: noop }`) — the
 * SAME shape, so no consumer branches on a new state: `currency === null` already
 * means "hold the money rendering" everywhere it is read.
 */
export function useTenantCurrency({
  enabled = true,
}: UseTenantCurrencyOptions = {}): TenantCurrency {
  // `useResource` is called unconditionally (stable hook order); the SKIP lives in
  // the fetcher, which resolves a null payload instead of calling the API, so a
  // disabled hook makes no request. `enabled` is a dep so a flip re-runs.
  const settings = useResource(
    useCallback(() => (enabled ? getTenantSettings() : Promise.resolve(null)), [enabled]),
    [enabled],
  );
  // Disabled: report the settled null shape from the FIRST render (the resolved-null
  // run would otherwise flash `loading`), and a `refetch` that cannot fetch.
  if (!enabled) {
    return { currency: null, status: 'ready', error: null, refetch: NOOP_REFETCH };
  }
  return {
    currency: settings.data?.currency ?? null,
    status: settings.status,
    error: settings.error,
    refetch: settings.refetch,
  };
}
